import { useState } from "react";
import { toast } from "sonner";
import { api, BRAND, formatApiErrorDetail } from "@/lib/api";
import { Phone, Mail, Globe, Send } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Thank you for reaching out 💛");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
      <div className="grid lg:grid-cols-2 gap-14">
        <div>
          <span className="text-xs tracking-[0.25em] uppercase text-[#8A8175] font-semibold">Contact</span>
          <h1 className="font-heading text-5xl sm:text-6xl text-[#3A3632] mt-3">We'd love to hear from you.</h1>
          <p className="text-lg text-[#6B655F] mt-5 leading-relaxed">Questions, custom orders, or corporate gifting — reach out anytime. We are so grateful you are here.</p>
          <div className="mt-10 space-y-5">
            <div className="text-[#3A3632] font-medium text-lg">{BRAND.owner}</div>
            <a href={`tel:${BRAND.phone}`} className="flex items-center gap-3 text-[#6B655F] hover:text-[#8E9A86]" data-testid="contact-phone"><span className="h-10 w-10 rounded-full bg-[#F2EFEB] flex items-center justify-center"><Phone className="h-5 w-5 text-[#8E9A86]" /></span>{BRAND.phone}</a>
            <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 text-[#6B655F] hover:text-[#8E9A86]" data-testid="contact-email"><span className="h-10 w-10 rounded-full bg-[#F2EFEB] flex items-center justify-center"><Mail className="h-5 w-5 text-[#8E9A86]" /></span>{BRAND.email}</a>
            <div className="flex items-center gap-3 text-[#6B655F]"><span className="h-10 w-10 rounded-full bg-[#F2EFEB] flex items-center justify-center"><Globe className="h-5 w-5 text-[#8E9A86]" /></span>{BRAND.site}</div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E5DFD5] p-8" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your name" testid="contact-name"><input required value={form.name} onChange={set("name")} className="gh-input" /></Field>
            <Field label="Email" testid="contact-email-input"><input type="email" required value={form.email} onChange={set("email")} className="gh-input" /></Field>
          </div>
          <Field label="Subject" testid="contact-subject"><input value={form.subject} onChange={set("subject")} className="gh-input" /></Field>
          <Field label="Message" testid="contact-message"><textarea required rows={5} value={form.message} onChange={set("message")} className="gh-input resize-none" /></Field>
          <button type="submit" disabled={busy} data-testid="contact-submit" className="w-full mt-2 bg-[#8E9A86] text-white rounded-full px-6 py-3.5 font-medium hover:bg-[#7A8772] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <Send className="h-4 w-4" /> {busy ? "Sending…" : "Send message"}
          </button>
        </form>
      </div>
      <style>{`.gh-input{width:100%;border:1px solid #D1CCC5;border-radius:0.6rem;background:#fff;padding:0.7rem 0.9rem;font-size:0.95rem;outline:none;transition:all .2s}.gh-input:focus{border-color:#8E9A86;box-shadow:0 0 0 2px rgba(142,154,134,.25)}`}</style>
    </div>
  );
}

const Field = ({ label, testid, children }) => (
  <label className="block mb-4" data-testid={testid}>
    <span className="text-sm text-[#3A3632] font-medium mb-1.5 block">{label}</span>
    {children}
  </label>
);
